import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import { FileDownload } from '@mui/icons-material';
import { ROLES } from '../../constants/roles';
import { extractSequenceFromCode, generateComponentCode } from './componentFormUtils';
import { useComponentFormHandlers } from './useComponentFormHandlers';
import BasicFieldsSection from './ComponentFormFields/BasicFieldsSection';
import DetailsFieldsSection from './ComponentFormFields/DetailsFieldsSection';
import TimeSinceRecordsSection from './ComponentFormFields/TimeSinceRecordsSection';
import AssemblyComparisonSection from './ComponentFormFields/AssemblyComparisonSection';
import ComponentSelfComparisonSection from './ComponentFormFields/ComponentSelfComparisonSection';
import AirframeComparisonSection from './ComponentFormFields/AirframeComparisonSection';
import LifecycleHistorySection from './ComponentFormFields/LifecycleHistorySection';
import { findAirframeAssembly } from './tsrComparisonUtils';
import { useTlbCycleOptions } from './hooks/useTlbCycleOptions';
import { recordUsesCyclesUnit } from '../../utils/tlbCycleCounters';
import { normalizeTsrLabel } from '../../utils/liveTlbUsageMapping';

const ComponentForm = ({
  open,
  onClose,
  selectedComponent,
  selectedAircraft = null,
  formData,
  setFormData,
  dropdownOptions,
  onSubmit,
  onDelete,
  user,
  assemblies = [],
  onRefreshDropdowns,
  onOpenAssemblyForm,
  readOnly = false,
  onSwitchToEdit,
  currentUsageTotals = null,
  effectiveTlbUsageMapping = null,
  onExportAircraftCsv,
  exporting = false,
  canExportAircraftCsv = false
}) => {
  const [timeSinceRecords, setTimeSinceRecords] = useState([]);
  const [labelOptions, setLabelOptions] = useState([]);
  // Track if we've already refreshed dropdowns for this dialog session
  const hasRefreshedRef = useRef(false);
  // Track if user has manually edited the code (don't auto-update if they have)
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  // Track if Original Equipment checkbox is checked
  const [isOriginalEquipment, setIsOriginalEquipment] = useState(false);

  // Refresh dropdowns when dialog opens (only once per open session)
  useEffect(() => {
    if (open && onRefreshDropdowns && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      onRefreshDropdowns();
    }
    // Reset when dialog closes
    if (!open) {
      hasRefreshedRef.current = false;
      setIsOriginalEquipment(false);
    }
  }, [open, onRefreshDropdowns]);

  useEffect(() => {
    if (selectedComponent && selectedComponent.timeSinceRecords) {
      // Ensure all records have limit field for backward compatibility
      const recordsWithLimit = selectedComponent.timeSinceRecords.map(record => ({
        ...record,
        limit: record.limit || { value: '00:00', unit: record.elapsedTime?.unit || 'Hours' }
      }));
      setTimeSinceRecords(recordsWithLimit);
    } else {
      setTimeSinceRecords([]);
    }
    
    // Reset code manual edit flag when component changes
    if (selectedComponent) {
      setCodeManuallyEdited(true); // Existing component code should not be auto-updated
    } else {
      setCodeManuallyEdited(false); // New component, allow auto-generation
    }
    
    // Reset Original Equipment checkbox when component changes
    setIsOriginalEquipment(false);
  }, [selectedComponent]);

  // Build label options for Time Since Records Label column:
  // - Master-defined columns (Assembly Type Configurations) for relevant assemblies
  // - Custom columns (Custom Assembly Type Configuration) on those assemblies
  useEffect(() => {
    if (!open) {
      setLabelOptions([]);
      return;
    }

    const assembliesList = Array.isArray(assemblies) ? assemblies : [];
    const relevantAssemblies = new Set();

    // Parent assembly of this component
    const parentAssembly = assembliesList.find((a) => {
      const assemblyIdStr = a._id ? String(a._id) : null;
      const formAssemblyIdStr = formData.assemblyId ? String(formData.assemblyId) : null;
      return assemblyIdStr && formAssemblyIdStr && assemblyIdStr === formAssemblyIdStr;
    });
    if (parentAssembly) {
      relevantAssemblies.add(parentAssembly);
    }

    // Airframe assembly
    const { assembly: airframeAssembly } = findAirframeAssembly(assembliesList || []);
    if (airframeAssembly) {
      relevantAssemblies.add(airframeAssembly);
    }

    // Engine assemblies (typeName contains "engine", case-insensitive)
    const engineAssemblies = assembliesList.filter(
      (a) =>
        typeof a.typeName === 'string' &&
        a.typeName.toLowerCase().includes('engine')
    );
    engineAssemblies.forEach((a) => relevantAssemblies.add(a));

    const customLabels = new Set();
    const assemblyTypeIds = new Set();

    for (const assembly of relevantAssemblies) {
      if (Array.isArray(assembly.customColumns)) {
        assembly.customColumns.forEach((col) => {
          const label = (col.label || '').trim();
          if (label) customLabels.add(label);
        });
      }

      if (assembly.type) {
        let typeId = assembly.type;
        if (typeof typeId === 'object' && typeId !== null) {
          typeId = typeId._id || typeId.id || typeId;
        }
        if (typeId) {
          assemblyTypeIds.add(String(typeId));
        }
      }
    }

    const fetchConfigs = async () => {
      const masterLabels = new Set();

      for (const typeId of assemblyTypeIds) {
        try {
          const res = await axios.get(
            `/api/master-data/assembly-type-configurations/assembly-type/${typeId}`
          );
          const columns = Array.isArray(res.data?.columns) ? res.data.columns : [];
          columns
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .forEach((col) => {
              const label = (col.label || '').trim();
              if (label) masterLabels.add(label);
            });
        } catch (err) {
          if (err.response?.status !== 404) {
            // Swallow non-404 errors to avoid blocking the form; logging can be added if needed
          }
        }
      }

      const combined = Array.from(
        new Set([
          ...Array.from(masterLabels),
          ...Array.from(customLabels)
        ])
      );

      setLabelOptions(combined);
    };

    fetchConfigs();
  }, [open, assemblies, formData.assemblyId]);

  // Auto-generate component code when both assemblyId and position are available
  useEffect(() => {
    // Only auto-generate for new components (not editing existing)
    if (!selectedComponent && !codeManuallyEdited && formData.assemblyId && formData.position) {
      const sequence = extractSequenceFromCode(formData.code || '');
      const generatedCode = generateComponentCode(formData.assemblyId, formData.position, sequence, assemblies);
      if (generatedCode && generatedCode !== formData.code) {
        setFormData(prev => ({ ...prev, code: generatedCode }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.assemblyId, formData.position, selectedComponent, codeManuallyEdited]);

  // Get all handlers from custom hook
  const {
    handleChange,
    handlePartMasterSelect,
    handleAddTimeSinceRecord,
    handleUpdateTimeSinceRecord,
    handleSelectTlbCycle,
    handleRemoveTimeSinceRecord,
    validateYearsInput,
    validateCyclesInput,
    validateCalendarDaysInput,
    handleNavigateToAddAssembly,
    handleNavigateToAddPart,
    handleNavigateToMasterData,
    handleSubmit
  } = useComponentFormHandlers({
    formData,
    setFormData,
    timeSinceRecords,
    setTimeSinceRecords,
    dropdownOptions,
    assemblies,
    codeManuallyEdited,
    setCodeManuallyEdited,
    isOriginalEquipment,
    setIsOriginalEquipment,
    selectedComponent,
    onClose,
    onOpenAssemblyForm,
    onSubmit
  });

  const aircraftId =
    selectedAircraft?._id ||
    formData?.aircraftId ||
    selectedComponent?.aircraftId?._id ||
    selectedComponent?.aircraftId;

  const { tlbCycleOptions, tlbCycleOptionsLoading } = useTlbCycleOptions({
    open,
    aircraftId,
    currentUsageTotals
  });

  // Backfill tlbCarriedForwardKey for legacy Cycles rows matched by TLB format label
  useEffect(() => {
    if (!open || !tlbCycleOptions.length) return;
    setTimeSinceRecords((prev) => {
      let changed = false;
      const next = prev.map((row) => {
        if (row.tlbCarriedForwardKey || !recordUsesCyclesUnit(row)) return row;
        const labelNorm = normalizeTsrLabel(row.label);
        const match = tlbCycleOptions.find((o) => normalizeTsrLabel(o.label) === labelNorm);
        if (!match) return row;
        changed = true;
        return { ...row, tlbCarriedForwardKey: match.key };
      });
      return changed ? next : prev;
    });
  }, [open, tlbCycleOptions, setTimeSinceRecords]);

  const canDelete = [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.MRO_MANAGER].includes(user?.role);
  const [lifecycleDialogOpen, setLifecycleDialogOpen] = useState(false);

  const parentAssembly = assemblies.find((a) => {
    const assemblyIdStr = a._id ? String(a._id) : null;
    const formAssemblyIdStr = formData.assemblyId ? String(formData.assemblyId) : null;
    return assemblyIdStr && formAssemblyIdStr && assemblyIdStr === formAssemblyIdStr;
  });

  const getDialogTitle = () => {
    if (!selectedComponent) return 'Add Component';
    if (readOnly) return 'Edit Component (View Only)';
    return 'Edit Component (Editable)';
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') onClose();
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        {getDialogTitle()}
      </DialogTitle>
      <DialogContent>
        <Box sx={readOnly ? { opacity: 0.9 } : undefined}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <BasicFieldsSection
              formData={formData}
              handleChange={handleChange}
              handlePartMasterSelect={handlePartMasterSelect}
              selectedComponent={selectedComponent}
              assemblies={assemblies}
              dropdownOptions={dropdownOptions}
              isOriginalEquipment={isOriginalEquipment}
              handleNavigateToAddAssembly={handleNavigateToAddAssembly}
              handleNavigateToMasterData={handleNavigateToMasterData}
              handleNavigateToAddPart={handleNavigateToAddPart}
              readOnly={readOnly}
            />
            <DetailsFieldsSection
              formData={formData}
              handleChange={handleChange}
              dropdownOptions={dropdownOptions}
              handleNavigateToMasterData={handleNavigateToMasterData}
              readOnly={readOnly}
            />
            <TimeSinceRecordsSection
              timeSinceRecords={timeSinceRecords}
              handleAddTimeSinceRecord={handleAddTimeSinceRecord}
              handleUpdateTimeSinceRecord={handleUpdateTimeSinceRecord}
              handleRemoveTimeSinceRecord={handleRemoveTimeSinceRecord}
              validateYearsInput={validateYearsInput}
              validateCyclesInput={validateCyclesInput}
              validateCalendarDaysInput={validateCalendarDaysInput}
              assemblies={assemblies}
              parentAssembly={parentAssembly}
              readOnly={readOnly}
              labelOptions={labelOptions}
              tlbCycleOptions={tlbCycleOptions}
              tlbCycleOptionsLoading={tlbCycleOptionsLoading}
              onSelectTlbCycle={handleSelectTlbCycle}
            />
            <AssemblyComparisonSection
              timeSinceRecords={timeSinceRecords}
              parentAssembly={parentAssembly}
              readOnly={readOnly}
            />
            <ComponentSelfComparisonSection
              timeSinceRecords={timeSinceRecords}
              readOnly={readOnly}
            />
            <AirframeComparisonSection
              timeSinceRecords={timeSinceRecords}
              assemblies={assemblies}
              readOnly={readOnly}
              currentUsageTotals={currentUsageTotals}
              effectiveTlbUsageMapping={effectiveTlbUsageMapping}
              liveDue={true}
            />
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        {selectedComponent && canExportAircraftCsv && (
          <Button
            onClick={onExportAircraftCsv}
            startIcon={<FileDownload />}
            disabled={exporting}
          >
            {exporting ? 'Downloading CSV...' : 'Download Aircraft CSV'}
          </Button>
        )}
        {selectedComponent && (selectedComponent.serialNumber || selectedComponent.serializedItemId) && (
          <Button onClick={() => setLifecycleDialogOpen(true)}>
            View Lifecycle
          </Button>
        )}
        {canDelete && selectedComponent && onDelete && !readOnly && (
          <Button onClick={onDelete} color="error">
            Archive
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        {readOnly ? (
          <Button
            onClick={onSwitchToEdit}
            variant="contained"
            disabled={!onSwitchToEdit}
          >
            Edit Component
          </Button>
        ) : (
          <Button onClick={handleSubmit} variant="contained">
            {selectedComponent ? 'Update' : 'Create'}
          </Button>
        )}
      </DialogActions>

      <Dialog open={lifecycleDialogOpen} onClose={() => setLifecycleDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Component Lifecycle History</DialogTitle>
        <DialogContent>
          <LifecycleHistorySection componentId={selectedComponent?._id} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLifecycleDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ComponentForm;
